import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { InfluencersIcon } from '../assets';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { LateralProfileMenu } from '../components/common/LateralProfileMenu';
import { ProposalSubrowTable } from '../components/proposals/ProposalSubrowsTable';
import DataTable from '../components/tables/DataTable';
import { getProposalsTableHeaders } from '../components/tables/headers/proposals';
import { replaceIds, ROUTES } from '../config/routes/config';
import { GET_PROPOSALS } from '../graphql/proposal/query';
import { GET_SINGLE_USER } from '../graphql/user/query';
import { useNotification } from '../hooks/useNotification';
import { usePermissions } from '../hooks/usePermissions';
import { useTableDataAndFilters } from '../hooks/useTableDataAndFilters';
import { proposalFilterAtom, userAtom } from '../states/atoms';
import type { ResponseSingleUser } from '../types';
import type {
  ProposalFiltersAtom,
  ProposalTable,
  ResProposalsList,
} from '../types/proposal';

export const SingleInfluencerProposals = () => {
  const { id } = useParams();
  const { t } = useTranslation('common');
  const myUser = useRecoilValue(userAtom);

  const navigate = useNavigate();
  const { notifyWarning } = useNotification();
  const location = useLocation();

  const { isInfluentia, isInfluencer } = usePermissions();
  const [user, setUser] = useState<any>({});
  const { data } = useQuery<ResponseSingleUser>(GET_SINGLE_USER, {
    variables: {
      id,
    },
    onError: () => {
      // TODO: SHOW MISSING PROPOSAL ID NOTIFICATION
      notifyWarning(t('global.errorSingleInfluencer'));
      navigate(ROUTES.INFLUENCERS);
    },
    onCompleted: (info: any) => {
      setUser(info.user.data);
    },
    skip: !id,
  });

  const {
    resetPagination,
    total,
    filters,
    setFilters,
    table,
    error,
    loading,
    refetch,
  } = useTableDataAndFilters<
    ProposalFiltersAtom,
    ProposalTable,
    {},
    ResProposalsList
  >({
    filterAtom: proposalFilterAtom,
    getColumns: getProposalsTableHeaders,
    columnsExtras: {},
    responseAccessKey: 'proposals',
    query: GET_PROPOSALS,
    subrowAccesor: 'ProposalUsers',
  });

  useEffect(() => {
    // Reset the shared proposalFilterAtom instead of merging with `prev`.
    // This atom is also used by the general Proposals list and by
    // SingleBrandProposals; if the user had a leftover client/status filter
    // set there, merging with `...prev` here would silently carry it over
    // and hide proposals that don't match that stale filter.
    setFilters({
      influencers: [{ label: '', value: data?.user.data.id || '' }],
    });
    return () => {
      resetPagination();
      setFilters({});
    };
  }, [user]);

  const handleTableClick = (idClick: string) => {
    navigate(
      replaceIds({
        url: ROUTES.SINGLE_PROPOSAL_GENERAL,
        params: [
          {
            proposalId: idClick,
          },
        ],
      }),
    );
  };

  return (
    <div className="w-full flex flex-col gap-y-5">
      <Breadcrumbs
        icon={<InfluencersIcon />}
        breadcrumbs={[
          {
            text: t('singleInfluencer.influencerBreadcrumps'),
            link: ROUTES.INFLUENCERS,
          },
          {
            text: data?.user.data.name || '',
            link: ROUTES.SINGLE_INFLUENCER.replace(':id', user.id),
          },
          {
            text: t('proposals.title'),
          },
        ]}
      />
      <div>
        <div className="flex flex-row gap-12">
          {user.id !== myUser.id &&
            isInfluentia() &&
            !location.pathname.includes('user') && (
              <div className="h-screen sticky top-0 lg:flex hidden">
                <LateralProfileMenu user={user} />
              </div>
            )}
          <div>
            <DataTable
              error={error}
              handleRowClick={(idClick) => handleTableClick(idClick)}
              handleSubrowClick={(idClick) => handleTableClick(idClick)}
              loading={loading}
              table={table}
              total={total}
              tableId="ProposalsTable"
              sorting={{ ...filters }}
              SubrowsComponent={ProposalSubrowTable}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
